import { Typography, Box, Paper, Alert, Grid, Divider } from '@mui/material';
import React from 'react';
import { BooleanInput, FormDataConsumer, Labeled, TextInput, SelectInput, maxLength, required } from 'react-admin';
import DateTimeInputUTC from '../../../components/DateTimeInputUTC';
import { RichTextInputCustom } from '../../../components/RichTextInputCustom';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const MAX_LENGTH_PUSH = 130;

const richTextSx = {
  width: 'auto',
  '& .ql-editor': {
    minHeight: 200
  }
};

const labeledWidthSx = {
  width: '70%'
};

const charCounterSx = {
  top: '35%',
  right: '1%',
  width: 'auto',
  fontSize: 12,
  opacity: 0.75,
  fontFamily: 'Helvetica',
  textAlign: 'end',
};

// Opções de tempo para o select (em minutos) - apenas para notificação de CONQUISTA
// 0 = envio imediato, mas pode demorar até 10 minutos para processar
const timeDelayChoices = [
  { id: '0', name: 'Imediatamente' },
  { id: '15', name: '15 minutos' },
  { id: '30', name: '30 minutos' },
  { id: '45', name: '45 minutos' },
  { id: '60', name: '60 minutos (1 hora)' },
  { id: '120', name: '2 horas' },
];

// Componente auxiliar para renderizar uma coluna de notificação
const NotificationColumn: React.FC<{
  type: 'creation' | 'achievement';
  icon: React.ReactNode;
  title: string;
  description: string;
  sourcePrefix: string;
  counterCharPush: number;
  onCounterChange: (value: number) => void;
}> = ({
  type,
  icon,
  title,
  description,
  sourcePrefix,
  counterCharPush,
  onCounterChange,
}) => {
  return (
    <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
      {/* Cabeçalho da Coluna */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        {icon}
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" paragraph>
        {description}
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Toggle para ativar esta notificação */}
      <BooleanInput
        source={`${sourcePrefix}.enabled`}
        label="Ativar esta notificação"
        defaultValue={false}
      />

      <FormDataConsumer>
        {({ formData }) => {
          const notificationData = sourcePrefix.split('.').reduce((obj, key) => obj?.[key], formData);

          if (!notificationData?.enabled) return null;

          return (
            <>
              <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
                Preencha os campos abaixo para configurar esta notificação.
              </Alert>

              {/* Título */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Conteúdo da Notificação
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  O título e a descrição serão exibidos na tela de notificações do aplicativo
                </Typography>
                <TextInput
                  source={`${sourcePrefix}.title`}
                  label="Título da notificação"
                  InputProps={{ autoComplete: 'off' }}
                  fullWidth
                  helperText="Este será o título principal da notificação"
                />
              </Box>

              {/* Descrição Rich Text */}
              <Box sx={richTextSx}>
                <Labeled label="Descrição: ">
                  <RichTextInputCustom
                    source={`${sourcePrefix}.body`}
                    label="Essa mensagem pode conter formatações e será exibida no aplicativo, na tela de notificação. Ela NÃO será enviada na notificação PUSH e no SMS"
                    validate={required()}
                  />
                </Labeled>
              </Box>

              {/* Mensagem Push - Sempre habilitada */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Mensagem Push
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Esta é a mensagem curta que aparecerá como alerta no dispositivo do usuário.
                  Seja direto e objetivo para chamar atenção.
                </Typography>
                <Labeled label="Texto do alerta push:" sx={labeledWidthSx}>
                  <>
                    <TextInput
                      style={{ width: '100%' }}
                      source={`${sourcePrefix}.pushMessage`}
                      label="Mensagem Push"
                      validate={[required(), maxLength(MAX_LENGTH_PUSH)]}
                      inputProps={{ autoComplete: 'off' }}
                      onChange={(e: any) => onCounterChange(e.target.value?.length || 0)}
                      helperText="Mensagem que aparecerá na barra de notificações do celular"
                    />
                    <Box component="span" sx={charCounterSx}>{counterCharPush}/{MAX_LENGTH_PUSH}</Box>
                  </>
                </Labeled>
              </Box>

              {/* Agendamento de Envio - DIFERENTE para criação vs conquista */}
              {type === 'creation' ? (
                // NOTIFICAÇÃO DE CRIAÇÃO: Enviar imediatamente ou agendar
                <Box sx={{ my: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Agendamento de Envio
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Escolha quando a notificação será disparada para os usuários
                  </Typography>
                  <BooleanInput
                    source={`${sourcePrefix}.sendNow`}
                    label="Enviar imediatamente"
                    defaultValue={true}
                  />
                  <FormDataConsumer>
                    {({ formData }) => {
                      const notification = sourcePrefix.split('.').reduce((obj, key) => obj?.[key], formData);

                      if (notification?.sendNow === false) {
                        // Se sendNow = false, mostrar DateTimeInput para agendar
                        // Calcular data mínima: agora + 10 minutos
                        const minDateTime = new Date(new Date().getTime() + 10 * 60000);

                        return (
                          <Box sx={{ mt: 2 }}>
                            <DateTimeInputUTC
                              source={`${sourcePrefix}.sendAt`}
                              label="Data e hora de envio"
                              validate={[required()]}
                              helperText="Selecione a data e hora para enviar a notificação (mínimo: agora + 10 minutos)"
                              defaultValue={minDateTime}
                            />
                            <Alert severity="info" sx={{ mt: 2 }}>
                              A notificação será enviada na data e hora selecionadas acima.
                            </Alert>
                          </Box>
                        );
                      } else if (notification?.sendNow) {
                        // Se sendNow = true, mostrar alerta
                        return (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            <strong>Atenção:</strong> A notificação pode demorar até 10 minutos para ser processada e enviada aos usuários.
                          </Alert>
                        );
                      }
                      return null;
                    }}
                  </FormDataConsumer>
                </Box>
              ) : (
                // NOTIFICAÇÃO DE CONQUISTA: Delay em minutos após a conquista
                <Box sx={{ my: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Agendamento de Envio
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Selecione quanto tempo após a conquista a notificação será enviada
                  </Typography>
                  <SelectInput
                    source={`${sourcePrefix}.delayMinutes`}
                    label="Tempo de envio"
                    choices={timeDelayChoices}
                    validate={[required()]}
                    defaultValue="0"
                    helperText="A notificação será enviada após o tempo selecionado da conquista do brinde"
                  />
                  <FormDataConsumer>
                    {({ formData }) => {
                      const notification = sourcePrefix.split('.').reduce((obj, key) => obj?.[key], formData);
                      const delayMinutes = parseInt(notification?.delayMinutes || '0');

                      if (delayMinutes === 0) {
                        return (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            <strong>Atenção:</strong> A notificação pode demorar até 10 minutos para ser processada e enviada aos usuários.
                          </Alert>
                        );
                      }
                      return null;
                    }}
                  </FormDataConsumer>
                </Box>
              )}
            </>
          );
        }}
      </FormDataConsumer>
    </Paper>
  );
};

const FormNotification: React.FC<{ formData?: any }> = ({ formData }) => {

  const [counterCharPushCreation, setCounterCharPushCreation] = React.useState(0);
  const [counterCharPushAchievement, setCounterCharPushAchievement] = React.useState(0);

  // Debug: Log dos dados do formulário quando o componente renderiza
  React.useEffect(() => {
    console.log('🔔 DEBUG FORM - FormNotification renderizado com formData:', {
      notificationCreation: formData?.notificationCreation,
      notificationAchievement: formData?.notificationAchievement
    });
  }, [formData]);

  return (
    <Box sx={{ py: 2 }}>
      {/* Cabeçalho Explicativo */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <NotificationsActiveIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Configuração de Notificações
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure dois tipos de notificações para este brinde: uma enviada quando o brinde é criado
          e outra quando o usuário conquista o brinde.
        </Typography>
      </Box>

      {/* Info Box */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: '#e3f2fd',
          border: '1px solid #90caf9',
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={600} gutterBottom>
          Tipos de Notificação:
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li><strong>Notificação de Criação:</strong> Enviada quando o brinde é criado/publicado, informando aos usuários que um novo brinde está disponível</li>
            <li><strong>Notificação de Conquista:</strong> Enviada automaticamente quando o usuário atinge os requisitos e conquista o brinde</li>
          </ul>
        </Typography>
      </Paper>

      {/* Grid com duas colunas ocupando mais espaço */}
      <Grid container spacing={4}>
        {/* Coluna 1: Notificação de Criação */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <NotificationColumn
            type="creation"
            icon={<CardGiftcardIcon color="primary" />}
            title="Notificação de Criação"
            description="Enviada quando o brinde é criado/publicado para informar os usuários"
            sourcePrefix="notificationCreation"
            counterCharPush={counterCharPushCreation}
            onCounterChange={setCounterCharPushCreation}
          />
        </Grid>

        {/* Coluna 2: Notificação de Conquista */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <NotificationColumn
            type="achievement"
            icon={<EmojiEventsIcon color="success" />}
            title="Notificação de Conquista"
            description="Enviada automaticamente quando o usuário conquista o brinde"
            sourcePrefix="notificationAchievement"
            counterCharPush={counterCharPushAchievement}
            onCounterChange={setCounterCharPushAchievement}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default React.memo(FormNotification);
